import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Sparkles, ArrowLeft, ArrowRight, AlertTriangle,
  TrendingUp, ShieldCheck, DollarSign, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import SEOHead, { generateBreadcrumbSchema, generateOptimizerServiceSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// Cloudflare Turnstile site key (free, no PII). When unset we skip the
// widget entirely — server also skips enforcement until the matching
// secret key is set in backend env, so the form keeps working.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY || '';

function MetricCard({ icon: Icon, label, value, sublabel, accent = 'cyan' }) {
  const accents = {
    cyan: 'border-[#00F0FF]/30 bg-[#00F0FF]/5',
    green: 'border-[#00FF94]/40 bg-[#00FF94]/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
  };
  return (
    <div className={`p-5 rounded-xl border ${accents[accent]} flex flex-col gap-2`}>
      <div className="flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-neutral-400">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="text-2xl sm:text-3xl font-bold text-white leading-tight">
        {value}
      </div>
      {sublabel ? (
        <p className="text-sm text-neutral-300 leading-relaxed">{sublabel}</p>
      ) : null}
    </div>
  );
}

function ScoreRing({ score }) {
  const pct = Math.max(0, Math.min(100, score));
  const color = pct >= 70 ? '#00FF94' : pct >= 40 ? '#00F0FF' : '#fbbf24';
  const circumference = 2 * Math.PI * 36;
  const offset = circumference * (1 - pct / 100);
  return (
    <div className="relative w-24 h-24 flex-shrink-0">
      <svg viewBox="0 0 80 80" className="w-full h-full transform -rotate-90">
        <circle cx="40" cy="40" r="36" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
        <circle
          cx="40" cy="40" r="36"
          fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold text-white">{pct}</span>
      </div>
    </div>
  );
}

export default function Optimizer() {
  const [form, setForm] = useState({
    product_url: '',
    description: '',
    name: '',
    _hp_company: '',  // honeypot — must stay empty
  });
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  // Cloudflare Turnstile token. null = no widget shown yet (or not
  // configured); '' = widget shown but visitor hasn't completed; string
  // = valid token ready to submit. Form submit waits for a token when
  // the widget is enabled.
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileWidgetRef = useRef(null);

  // Lazy-load the Turnstile script + render the invisible widget only
  // when a site key is configured. Cleans up the script tag on unmount.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    const renderWidget = () => {
      if (!window.turnstile || !turnstileWidgetRef.current) return;
      try {
        window.turnstile.render(turnstileWidgetRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          size: 'invisible',
          callback: (token) => setTurnstileToken(token),
          'error-callback': () => setTurnstileToken(null),
          'expired-callback': () => setTurnstileToken(null),
        });
        // Pre-fire so we have a token by the time the user clicks submit.
        // Falls back to lazy retrieval inside `run` if this races.
        if (window.turnstile.execute) {
          try { window.turnstile.execute(turnstileWidgetRef.current); } catch { /* swallow */ }
        }
      } catch (e) {
        /* Turnstile init errors must not block the form */
      }
    };
    if (existing) { renderWidget(); return; }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=__theailedgerTurnstileReady';
    script.async = true;
    script.defer = true;
    window.__theailedgerTurnstileReady = renderWidget;
    document.head.appendChild(script);
    return () => {
      try { delete window.__theailedgerTurnstileReady; } catch { /* swallow */ }
    };
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async (e) => {
    e.preventDefault();
    if (form.description.trim().length < 20) {
      toast.error('Tell us a bit more about your tool (at least 20 characters).');
      return;
    }
    setLoading(true);
    setReport(null);
    try {
      const res = await axios.post(`${API_URL}/api/audit/preview`, {
        product_url: form.product_url,
        description: form.description,
        name: form.name || null,
        _hp_company: form._hp_company || '',
        turnstile_token: turnstileToken || null,
      });
      if (res.data.status !== 'ok') {
        toast.error(res.data.error || 'Could not generate estimate. Try again in a moment.');
        return;
      }
      setReport(res.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error("You've hit today's free limit (10 runs/day). Apply as a Founding Creator to get the full report.");
      } else {
        toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const trackSubmitClick = () => {
    // Fire-and-forget — don't block navigation if analytics fails.
    try {
      axios.post(`${API_URL}/api/audit/track-event`, {
        event_type: 'submit_cta_click',
        product_url: form.product_url || null,
      }).catch(() => {});
    } catch {
      /* swallow */
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <SEOHead
        title="Free AI Tool Listing Optimizer — estimate your monthly earnings | theailedger.ai"
        description="Paste your AI tool's URL and get a free instant estimate: SEO score, conservative monthly earnings range, and the top trust signals your listing is missing. No signup required."
        path="/optimizer"
        keywords="free AI tool audit, AI listing optimizer, AI SEO audit, AI tool earnings estimate, AI SaaS audit, AI product listing analyzer, AI tool review free, theailedger optimizer"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            generateOptimizerServiceSchema(),
            generateBreadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Free AI Listing Optimizer', path: '/optimizer' },
            ]),
          ],
        }}
      />
      <div className="px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm mb-6"
            data-testid="optimizer-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>

          <div className="inline-block px-3 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-xs font-bold tracking-wider uppercase mb-4">
            Free AI Tool Audit
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold mb-3 tracking-tight" data-testid="optimizer-heading">
            What could your AI tool realistically earn?
          </h1>
          <p className="text-neutral-300 text-base sm:text-lg leading-relaxed mb-8 max-w-2xl">
            Paste your URL. We'll audit your listing for SEO + trust, and give you a
            conservative monthly earnings range based on category benchmarks. Free, no signup.
          </p>

          <form
            onSubmit={run}
            className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 sm:p-6 space-y-4 mb-8"
          >
            <div>
              <label className="block text-sm font-semibold mb-1.5">Product URL</label>
              <input
                type="url"
                required
                placeholder="https://your-ai-tool.com"
                value={form.product_url}
                onChange={update('product_url')}
                data-testid="optimizer-input-url"
                className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF]"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">
                What does your tool do? <span className="text-neutral-500 font-normal">(1–2 sentences)</span>
              </label>
              <textarea
                required
                rows={3}
                placeholder="e.g. We auto-generate SOAP notes for veterinary clinics from voice recordings, $49/mo."
                value={form.description}
                onChange={update('description')}
                data-testid="optimizer-input-description"
                className="w-full bg-black/40 border border-white/20 rounded-lg px-3 py-2.5 text-white focus:outline-none focus:border-[#00F0FF] resize-none"
              />
              <p className="text-xs text-neutral-500 mt-1">
                The more specific you are, the more accurate your earnings estimate.
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="optimizer-submit"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#00F0FF]/90 transition disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? 'Analyzing your listing…' : 'Get my free estimate'}
            </button>

            {/* Honeypot field — hidden from humans, irresistible to bots.
                Filled values are silently routed to a decoy response on
                the backend so the bot operator can't tell their script
                was caught. Multiple defenses (display:none, aria-hidden,
                tabindex:-1, autocomplete:off, off-screen position) to
                survive common bot heuristics. */}
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: 0, height: 0, overflow: 'hidden' }}
            >
              <label htmlFor="company">Company (leave blank)</label>
              <input
                type="text"
                id="company"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                value={form._hp_company}
                onChange={update('_hp_company')}
              />
            </div>

            {/* Cloudflare Turnstile invisible widget container — renders
                only when REACT_APP_CLOUDFLARE_TURNSTILE_SITE_KEY is set.
                When unset, this div stays empty and server-side
                enforcement is also bypassed (zero friction until keys
                are obtained). */}
            {TURNSTILE_SITE_KEY ? (
              <div
                ref={turnstileWidgetRef}
                data-testid="optimizer-turnstile"
                style={{ display: 'inline-block' }}
              />
            ) : null}

            <p className="text-xs text-neutral-500">
              Most reports return in under 10 seconds. Free, no signup, limited to 10 estimates per visitor per day.
            </p>
          </form>

          {report ? (
            <div className="space-y-4" data-testid="optimizer-report">
              <div className="flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-[#00F0FF] mb-1">
                <Sparkles className="w-3 h-3" />
                Your free preview
              </div>

              <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 sm:p-6 flex items-center gap-5">
                <ScoreRing score={report.seo_score || 0} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs uppercase tracking-wider text-neutral-400 mb-1">SEO + listing readiness</div>
                  <div className="text-lg sm:text-xl font-bold text-white">
                    {report.seo_score >= 70
                      ? 'Strong — launch-ready'
                      : report.seo_score >= 40
                      ? 'Solid base — a few fixes will unlock more search traffic'
                      : 'Needs work — buyers and search engines will struggle to find you'}
                  </div>
                  <p className="text-sm text-neutral-400 mt-1 leading-relaxed">
                    Score blends SEO signals, trust markers, and pricing clarity from your live page.
                  </p>
                </div>
              </div>

              <MetricCard
                icon={DollarSign}
                label="Conservative monthly earnings (months 1–3)"
                value={report.earnings_estimate?.monthly_range_usd || '—'}
                sublabel={report.earnings_estimate?.reasoning}
                accent="green"
              />

              {report.earnings_estimate?.category_benchmark ? (
                <div className="text-xs text-neutral-400 -mt-2 ml-1 leading-relaxed">
                  <span className="font-bold text-neutral-300">Category benchmark:</span>{' '}
                  {report.earnings_estimate.category_benchmark}
                </div>
              ) : null}

              {report.top_title_suggestion ? (
                <MetricCard
                  icon={TrendingUp}
                  label="One title rewrite to try first"
                  value={`"${report.top_title_suggestion}"`}
                  sublabel="Replaces your current listing title to improve click-through rate from search."
                  accent="cyan"
                />
              ) : null}

              {Array.isArray(report.top_missing_trust_signals) && report.top_missing_trust_signals.length > 0 ? (
                <div className="p-5 rounded-xl border border-amber-500/30 bg-amber-500/5">
                  <div className="flex items-center gap-2 text-xs font-bold tracking-wider uppercase text-amber-400 mb-3">
                    <ShieldCheck className="w-4 h-4" />
                    Top 3 trust signals you're missing
                  </div>
                  <ul className="space-y-2">
                    {report.top_missing_trust_signals.slice(0, 3).map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-neutral-200">
                        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {!report.scraped?.fetched ? (
                <p className="text-xs text-neutral-500 px-1">
                  Note: we couldn't fully load your page (it may be a heavy SPA or block automated visitors). The
                  estimate is based on your description alone — full report after signup uses our deeper analysis pipeline.
                </p>
              ) : null}

              <div className="mt-6 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-[#00F0FF]/10 to-[#00FF94]/5 border border-[#00F0FF]/30">
                <div className="text-xs font-bold tracking-wider uppercase text-[#00F0FF] mb-2">
                  Want the full report?
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                  Apply as a Founding Creator — free, &lt; 3 minutes
                </h3>
                <p className="text-neutral-300 text-sm sm:text-base leading-relaxed mb-5">
                  You'll get the complete analysis: <b>3 title rewrites · 3 description rewrites · pricing
                  benchmark · 5-item launch-week checklist · live listing on theailedger.ai</b>. We re-run
                  the optimizer on submission with our deeper analysis pipeline.
                </p>
                <ul className="space-y-2 mb-6">
                  {[
                    'Founding Creator badge on your listing',
                    'Featured on our homepage carousel (first 90 days)',
                    'Direct affiliate link with our buyer audience',
                    'No fees on your first 10 customers',
                  ].map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-neutral-200">
                      <Check className="w-4 h-4 text-[#00FF94] mt-0.5 flex-shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/submit"
                  onClick={trackSubmitClick}
                  data-testid="optimizer-cta-apply"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#00F0FF]/90 transition"
                >
                  Apply as a Founding Creator
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
